import React, { useEffect, useState } from 'react';
import Close from '../../../Assets/Modals/closeIcon.svg';
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from "@mui/x-date-pickers";
import { CalendarIcon } from '@mui/x-date-pickers';
import { useDispatch, useSelector } from 'react-redux';
import { closeModal } from '../../../Features/orderManagementSlice';
import { showToast } from '../../../Components/Toaster/Toaster'; 
import * as XLSX from "xlsx";
import { formatDateToIST, getOrderStatus, formatDate } from '../../../lib/utils/utils';

function convertTimestampToISO(timestamp) {
    const date = new Date(timestamp);
    return date.toISOString();
}

function ExportModal() {
    const dispatch = useDispatch();

    const dates = {
        from: null,
        to: null,
    }

    const [date, setDate] = useState(dates);
    const [selectedExportType, setSelectedExportType] = useState('');
    const [buttonAvailable, setButtonAvailable] = useState(false);

    const ordersList = useSelector((state) => state.orderManagement.orders.orders);

    const handleRadioSelection = (e) => {
        setSelectedExportType(e.target.value);
    }

    const handleCloseButtonClick = () => {
        dispatch(closeModal());
    };

    const handleExportButton = () => {
        const fromDate = new Date(date.from);
        const toDate = new Date(date.to);
        const currentDate = new Date();

        const fromTimestamp = fromDate.getTime();
        const toTimestamp = toDate.getTime();

        if (fromTimestamp || toTimestamp) {
            if (!fromTimestamp) {
                showToast('Select start date.', 'warning');
                return;
            }
            else if (!toTimestamp) {
                showToast('Select end date.', 'warning');
                return;
            }
            else if (fromDate > toDate) {
                showToast('Start date should be earlier than end date.', 'warning');
                return;
            }
            else if (currentDate > toDate) {
                showToast('End date should not be in the past.', 'warning');
                return;
            }
            else {
                if(selectedExportType) {
                    const fromDateObj = new Date(fromDate);
                    const toDateObj = new Date(toDate);

                    const filteredList = ordersList.filter(order => {
                        const orderDate = new Date(order.createdAt);
                        return orderDate >= fromDateObj && orderDate <= toDateObj;
                    });

                    if (filteredList.length > 0) {
                        if(selectedExportType === 'CSV') {
                            let fromDateISO = convertTimestampToISO(fromTimestamp);
                            let toDateISO = convertTimestampToISO(toTimestamp);
                            const rows = filteredList.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).map((order, index) => ({
                                index: index + 1,
                                orderId: `#${order.orderId.slice(-6)}`,
                                orderDate: formatDateToIST(order.createdAt),
                                orderStatus: getOrderStatus(order.orderStatus),
                                price: order.price,
                            }));

                            const workbook = XLSX.utils.book_new();
                            const worksheet = XLSX.utils.json_to_sheet(rows);

                            XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");

                            XLSX.utils.sheet_add_aoa(worksheet, [
                                ["S. no.", "Order ID", "Order Date", "Order Status", "Total Price"],
                            ]);

                            XLSX.writeFile(workbook, `Orders-${formatDate(fromDateISO)}-${formatDate(toDateISO)}.xlsx`, { compression: true });
                            dispatch(closeModal());
                        }
                        else {
                            showToast('Export to PDF currently unavailable.', 'error');
                        }
                    }
                    else {
                        showToast('No orders found in selected date range.', 'warning');
                    }
                }
                else {
                    showToast('Select export type.', 'warning');
                }
            }
        }
        else {
            showToast('Select dates to continue.', 'warning');
            return;
        }
    }

    useEffect(() => {
        if(date.from && date.to && selectedExportType) {
            setButtonAvailable(true);
        }
        else {
            setButtonAvailable(false);
        }
    }, [date, selectedExportType]);

    return (
        <div className="w-1/2 h-fit bg-[#FFFFFF] rounded-xl p-6 flex flex-col gap-6 shadow-[0px_0px_12px_0px_#0000003D]">
            <div className="flex items-center justify-between">
                <h1 className='font-urbanist-regular font-semibold text-2xl text-[#121212]'>Export Orders</h1>

                <button onClick={handleCloseButtonClick}>
                    <img src={Close} alt="Close Modal" />
                </button>
            </div>

            <form className="font-urbanist-regular flex flex-col gap-3">
                <div className="flex flex-col gap-3">
                    <h1 className='font-medium text-[16px] leading-8 text-[#121212]'>Select Date</h1>

                    <div className="flex gap-8">
                        <div className="flex flex-col gap-1">
                            <h1 className='font-normal text-base text-[#121212]'>From</h1>

                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DemoContainer components={['DatePicker']}>
                                    <DatePicker
                                        label="Start Date" 
                                        onChange={e => setDate({ ...date, from: e.$d })} 
                                        slots={{ openPickerIcon: CalendarIcon }} 
                                        sx={{
                                            width: '180px'
                                        }}
                                    />
                                </DemoContainer>
                            </LocalizationProvider>
                        </div>

                        <div className="flex flex-col gap-1">
                            <h1 className='font-normal text-base text-[#121212]'>To</h1>

                            <LocalizationProvider dateAdapter={AdapterDayjs}>
                                <DemoContainer components={['DatePicker']}>
                                    <DatePicker
                                        label="End Date" 
                                        onChange={e => setDate({ ...date, to: e.$d })} 
                                        slots={{ openPickerIcon: CalendarIcon, }} 
                                        sx={{
                                            width: '180px',
                                        }}
                                    />
                                </DemoContainer>
                            </LocalizationProvider>
                        </div>
                    </div>
                </div>

                <div className="flex flex-col gap-3">
                    <h1 className='font-medium text-[16px] leading-8 text-[#121212]'>Export as</h1>

                    <div className="flex gap-6">
                        <label className="m-0 flex items-center gap-2 cursor-pointer" htmlFor="CSV">
                            <input 
                                value='CSV' 
                                type="radio" 
                                name="CSV" 
                                id="CSV" 
                                checked={selectedExportType === 'CSV'} 
                                onChange={handleRadioSelection} 
                                className='hidden' 
                            />

                            <div className={`w-4 h-4 flex items-center justify-center rounded-full border ${selectedExportType === 'CSV' ? 'border-primaryColor' : 'border-[#757575]'}`}>
                                {selectedExportType === 'CSV' && <div className="w-2 h-2 rounded-full bg-primaryColor"></div>}
                            </div>
                            CSV
                        </label>

                        <label className="m-0 flex items-center gap-2 cursor-pointer" htmlFor="PDF">
                            <input 
                                value='PDF' 
                                type="radio" 
                                name="PDF" 
                                id="PDF" 
                                checked={selectedExportType === 'PDF'} 
                                onChange={handleRadioSelection} 
                                className='hidden' 
                            />

                            <div className={`w-4 h-4 flex items-center justify-center rounded-full border ${selectedExportType === 'PDF' ? 'border-primaryColor' : 'border-[#757575]'}`}>
                                {selectedExportType === 'PDF' && <div className="w-2 h-2 rounded-full bg-primaryColor"></div>}
                            </div>
                            PDF
                        </label>
                    </div>
                </div>
            </form>

            <button onClick={handleExportButton} className={`${buttonAvailable ? 'bg-primaryColor hover:bg-primaryAccent' : 'bg-buttonDisabledBg cursor-not-allowed'} text-buttonText px-4 py-2 rounded font-semibold text-base text-center`} type='button'>
                Export
            </button>
        </div>
    );
}

export default ExportModal;
